import React, { ReactElement, ReactNode, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { useReducedMotion } from './useReducedMotion';

export interface SectionRevealProps {
  children: ReactNode;

  index?: number;
  sx?: SxProps;
  component?: React.ElementType;
  ariaLabel?: string;

  revealKey?: string;
}

const playedRevealKeys = new Set<string>();

export const SectionReveal = ({
  children,
  index = 0,
  sx,
  component = 'section',
  ariaLabel,
  revealKey,
}: SectionRevealProps): ReactElement => {
  const reduced = useReducedMotion();
  const delay = `${index * 40}ms`;

  const key = revealKey ?? ariaLabel;

  const [shouldAnimate] = useState(() => {
    if (!key) return true;
    if (playedRevealKeys.has(key)) return false;
    playedRevealKeys.add(key);
    return true;
  });

  return (
    <Box
      component={component}
      aria-label={ariaLabel}
      sx={{
        '@keyframes forYouFadeUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes forYouFade': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        animation: shouldAnimate
          ? reduced
            ? `forYouFade 80ms ease ${delay} both`
            : `forYouFadeUp 240ms ease ${delay} both`
          : 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
